import { formatNumber, kgToLb, lbToKg, type PatientPopulation } from '@ems/clinical';
import { Eraser, Timer, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PATIENT_TTL_MS, usePatient, usePatientStore } from '@/stores/patient';
import { usePrefs } from '@/stores/prefs';
import { cn } from '@/lib/utils';
import { Badge, Button, Select } from './ui';

const POPULATION_LABEL: Record<PatientPopulation, string> = { adult: 'Adult', pediatric: 'Pediatric', neonate: 'Neonate' };

/**
 * Text state for a numeric field that tolerates partial input ("0.", "1.") while typing.
 * `factor` converts the stored canonical value to the displayed unit (e.g. kg → lb). The text is
 * only overwritten when the stored value or unit changes from elsewhere (clear button, peds tool).
 */
function useNumericText(value: number | undefined, factor: number) {
  const display = (v: number | undefined) => (v === undefined ? '' : String(Number((v * factor).toFixed(1))));
  const [text, setText] = useState(() => display(value));
  useEffect(() => {
    setText((prev) => {
      if (value === undefined) return prev === '' || Number(prev) <= 0 || Number.isNaN(Number(prev)) ? prev : '';
      return Math.abs(Number(prev) - value * factor) > 0.05 ? display(value) : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, factor]);
  return [text, setText] as const;
}

/**
 * Shared patient context for every calculator. Weight is always stored in kilograms; pounds are a
 * display convenience. The context lives in session storage and expires after inactivity.
 */
export function PatientBar({ className }: { className?: string }) {
  const patient = usePatient();
  const { weightKg, ageYears, populationOverride, updatedAt, update, clear } = usePatientStore();
  const { weightUnit, setWeightUnit } = usePrefs();
  const [ageUnit, setAgeUnit] = useState<'y' | 'mo'>(ageYears !== undefined && ageYears < 2 ? 'mo' : 'y');
  const [now, setNow] = useState(() => Date.now());

  const [weightText, setWeightText] = useNumericText(weightKg, weightUnit === 'lb' ? kgToLb(1) : 1);
  const [ageText, setAgeText] = useNumericText(ageYears, ageUnit === 'mo' ? 12 : 1);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (updatedAt && now - updatedAt > PATIENT_TTL_MS) clear();
  }, [now, updatedAt, clear]);

  const minutesLeft = updatedAt ? Math.max(0, Math.round((PATIENT_TTL_MS - (now - updatedAt)) / 60000)) : null;
  const implausible =
    weightKg !== undefined && ageYears !== undefined && ageYears < 1 && weightKg > 15
      ? 'Weight is high for an infant — confirm kg vs lb.'
      : weightKg !== undefined && weightKg > 300
        ? 'Weight exceeds 300 kg — confirm units.'
        : null;

  return (
    <section
      aria-label="Patient context"
      className={cn(
        'rounded-md border border-stone-300 bg-white p-3 dark:border-stone-700 dark:bg-ink-900',
        className,
      )}
    >
      <div className="mb-2.5 flex flex-wrap items-center gap-2">
        <UserRound className="size-3.5 text-stone-400" />
        <span className="label-caps text-stone-600 dark:text-stone-300">Patient</span>
        {patient.population && <Badge tone="brand">{POPULATION_LABEL[patient.population]}</Badge>}
        {patient.weightSource && patient.weightSource !== 'stated' && <Badge tone="neutral">{patient.weightSource}</Badge>}
        {minutesLeft !== null && (
          <span className="ml-auto inline-flex items-center gap-1 whitespace-nowrap font-mono text-xs text-stone-500" title={`Patient context clears automatically in ${minutesLeft} minutes`}>
            <Timer className="size-3.5" /> {minutesLeft} min
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-[1fr_1fr_auto_auto]">
        <label className="block">
          <span className="sr-only">Weight</span>
          <div className="flex h-12 overflow-hidden rounded-md border border-stone-300 bg-white focus-within:border-stone-900 focus-within:ring-1 focus-within:ring-stone-900 dark:border-stone-700 dark:bg-ink-950 dark:focus-within:border-stone-200 dark:focus-within:ring-stone-200">
            <input
              inputMode="decimal"
              type="number"
              step="any"
              placeholder="Weight"
              aria-label={`Weight in ${weightUnit}`}
              value={weightText}
              onChange={(e) => {
                setWeightText(e.target.value);
                const n = Number(e.target.value);
                if (e.target.value === '') update({ weightKg: undefined, weightSource: undefined });
                else if (Number.isFinite(n) && n > 0) update({ weightKg: weightUnit === 'lb' ? lbToKg(n) : n, weightSource: 'stated' });
              }}
              className="min-w-0 flex-1 bg-transparent px-3 text-lg font-semibold tabular-nums outline-none"
            />
            <button
              type="button"
              onClick={() => setWeightUnit(weightUnit === 'kg' ? 'lb' : 'kg')}
              className="border-l border-stone-200 px-3 font-mono text-sm font-medium text-stone-600 hover:bg-stone-100 hover:text-stone-950 dark:border-stone-700 dark:text-stone-300 dark:hover:bg-ink-800 dark:hover:text-white"
              aria-label="Toggle weight unit"
            >
              {weightUnit}
            </button>
          </div>
          {weightUnit === 'lb' && weightKg !== undefined && (
            <span className="mt-0.5 block font-mono text-xs text-stone-600 dark:text-stone-300">= {formatNumber(weightKg)} kg</span>
          )}
        </label>

        <label className="block">
          <span className="sr-only">Age</span>
          <div className="flex h-12 overflow-hidden rounded-md border border-stone-300 bg-white focus-within:border-stone-900 focus-within:ring-1 focus-within:ring-stone-900 dark:border-stone-700 dark:bg-ink-950 dark:focus-within:border-stone-200 dark:focus-within:ring-stone-200">
            <input
              inputMode="decimal"
              type="number"
              step="any"
              placeholder="Age"
              aria-label={`Age in ${ageUnit === 'y' ? 'years' : 'months'}`}
              value={ageText}
              onChange={(e) => {
                setAgeText(e.target.value);
                const n = Number(e.target.value);
                if (e.target.value === '') update({ ageYears: undefined });
                else if (Number.isFinite(n) && n >= 0) update({ ageYears: ageUnit === 'mo' ? n / 12 : n });
              }}
              className="min-w-0 flex-1 bg-transparent px-3 text-lg font-semibold tabular-nums outline-none"
            />
            <button
              type="button"
              onClick={() => setAgeUnit(ageUnit === 'y' ? 'mo' : 'y')}
              className="border-l border-stone-200 px-3 font-mono text-sm font-medium text-stone-600 hover:bg-stone-100 hover:text-stone-950 dark:border-stone-700 dark:text-stone-300 dark:hover:bg-ink-800 dark:hover:text-white"
              aria-label="Toggle age unit"
            >
              {ageUnit === 'y' ? 'yr' : 'mo'}
            </button>
          </div>
        </label>

        <Select
          aria-label="Population"
          value={populationOverride ?? 'auto'}
          onChange={(e) => update({ populationOverride: e.target.value === 'auto' ? undefined : (e.target.value as PatientPopulation) })}
          className="h-12"
        >
          <option value="auto">Auto{patient.population && !populationOverride ? ` (${POPULATION_LABEL[patient.population]})` : ''}</option>
          <option value="adult">Adult</option>
          <option value="pediatric">Pediatric</option>
          <option value="neonate">Neonate</option>
        </Select>

        <Button variant="outline" className="h-12" onClick={clear} icon={<Eraser className="size-4" />} disabled={!patient.hasData && !populationOverride}>
          Clear
        </Button>
      </div>

      {implausible && <p className="mt-2 text-sm font-medium text-amber-700 dark:text-amber-300">{implausible}</p>}
    </section>
  );
}
